import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { type VariantProps, cva, cx } from "class-variance-authority";
import { Button } from "../Button/Button";
import type { buttonVariants } from "../Button/Button";
import { Cancel as CancelIcon } from "../Icon/Cancel";
import { Close as CloseIcon } from "../Icon/Close";
import { Info as InfoIcon } from "../Icon/Info";
import { Star as StarIcon } from "../Icon/Star";
import { TickCircle as TickCircleIcon } from "../Icon/TickCircle";
import { Warning as WarningIcon } from "../Icon/Warning";

import * as styles from "./Toast.module.scss";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cx(styles.viewport, className)}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(styles.toast, {
  variants: {
    variant: {
      danger: styles.danger,
      info: styles.info,
      success: styles.success,
      warning: styles.warning,
      promotion: styles.promotion,
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

const variantIconMap = {
  danger: CancelIcon,
  warning: WarningIcon,
  info: InfoIcon,
  success: TickCircleIcon,
  promotion: StarIcon,
};

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants> & {
      icon?: React.ComponentType<{ className?: string }>;
      priority?: "default" | "low";
    }
>(({ className, variant, icon, priority, children, ...props }, ref) => {
  const Icon = icon ?? variantIconMap[variant || "info"];

  return (
    <ToastPrimitives.Root
      data-variant={variant || "info"}
      data-priority={priority || "default"}
      ref={ref}
      className={cx(toastVariants({ variant }), styles.toastRoot, className)}
      {...props}
    >
      <Icon className={styles.icon} />
      {children}
    </ToastPrimitives.Root>
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action> &
    VariantProps<typeof buttonVariants>
>(({ className, children, variant, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cx(className)}
    asChild
    data-variant={variant}
    {...props}
  >
    <Button
      size={`md`}
      className={styles.action}
      variant={variant || "primary"}
    >
      {children}
    </Button>
  </ToastPrimitives.Action>
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cx(className)}
    toast-close=""
    onClick={(e) => {
      e.stopPropagation();
      props.onClick?.(e);
    }}
    {...props}
    asChild
  >
    <Button className={styles.close} variant={"blurred"}>
      <CloseIcon />
    </Button>
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cx(styles.title, className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cx(styles.description, className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};

export type { ToastProps, ToastActionElement };
